// client/src/components/ProductRecommendations.jsx
import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { API_URL, getImageUrl, getPlaceholderImage } from '../config';
import PriceDisplay from './PriceDisplay';
import './ProductRecommendations.css';

const ProductRecommendations = ({
  productId,
  type = 'similar',
  title,
  userEmail,
  cartItems = []
}) => {
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchRecommendations();
  }, [productId, type, userEmail]);

  const fetchRecommendations = async () => {
    setLoading(true);
    setError(null);

    try {
      let url;
      let options = { method: 'GET' };

      switch (type) {
        case 'similar':
          url = `${API_URL}/recommendations/product/${productId}?limit=8`;
          break;
        case 'frequently-bought':
          url = `${API_URL}/recommendations/frequently-bought-together/${productId}?limit=4`;
          break;
        case 'trending':
          url = `${API_URL}/recommendations/trending`;
          break;
        case 'personalized':
          if (!userEmail) {
            setLoading(false);
            return;
          }
          url = `${API_URL}/recommendations/personalized/${userEmail}?limit=10`;
          break;
        case 'cart-based':
          url = `${API_URL}/recommendations/cart-based?limit=6`;
          options = {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ cartItems })
          };
          break;
        default:
          url = `${API_URL}/recommendations/product/${productId}?limit=8`;
      }

      const response = await fetch(url, options);
      if (!response.ok) throw new Error('Failed to fetch recommendations');

      const data = await response.json();
      setRecommendations(data.recommendations || []);
    } catch (err) {
      console.error('Error fetching recommendations:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getTitle = () => {
    if (title) return title;

    switch (type) {
      case 'similar':
        return 'You May Also Like';
      case 'frequently-bought':
        return 'Frequently Bought Together';
      case 'trending':
        return 'Trending Products';
      case 'personalized':
        return 'Recommended For You';
      case 'cart-based':
        return 'Complete Your Order';
      default:
        return 'Recommendations';
    }
  };

  if (loading) {
    return (
      <div className="recommendations-section">
        <h3>{getTitle()}</h3>
        <div className="text-center py-4">
          <Spinner animation="border" variant="primary" />
        </div>
      </div>
    );
  }

  if (error || recommendations.length === 0) {
    return null; // Don't show anything if there's an error or no recommendations
  }

  return (
    <div className="recommendations-section">
      <div className="recommendations-header">
        <h3>
          <i className="bi bi-lightbulb me-2"></i>
          {getTitle()}
        </h3>
        {type === 'trending' && (
          <span className="badge bg-danger trending-badge">
            <i className="bi bi-fire me-1"></i>Hot
          </span>
        )}
      </div>

      <Row className="g-3">
        {recommendations.map((product) => (
          <Col key={product._id} xs={6} md={4} lg={3}>
            <Card className="recommendation-card h-100 border-0 shadow-sm">
              <div className="recommendation-image-wrapper">
                <Card.Img
                  variant="top"
                  src={getImageUrl(product.image)}
                  alt={product.name}
                  className="recommendation-image"
                  onClick={() => navigate(`/product/${product._id}`)}
                  onError={(e) => {
                    e.target.src = getPlaceholderImage('No Image');
                  }}
                />
                {product.stock === 0 && (
                  <div className="out-of-stock-overlay">
                    <span className="badge bg-danger">Out of Stock</span>
                  </div>
                )}
                {product.trending?.totalSold && (
                  <div className="trending-badge-overlay">
                    <span className="badge sold-badge">
                      {product.trending.totalSold} sold
                    </span>
                  </div>
                )}
              </div>

              <Card.Body className="d-flex flex-column">
                <Card.Title
                  className="recommendation-title"
                  onClick={() => navigate(`/product/${product._id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  {product.name}
                </Card.Title>

                <div className="mt-auto">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <PriceDisplay price={product.price} className="recommendation-price" showDiscountLabel={true} />
                    {product.rating && (
                      <small className="text-warning">
                        ★ {product.rating.toFixed(1)}
                      </small>
                    )}
                  </div>

                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="w-100"
                    onClick={() => navigate(`/product/${product._id}`)}
                  >
                    View Details
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default ProductRecommendations;
